import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { IRootState } from 'src/reducers';
import PersonnelSearch from '../components/PersonnelSearch';
// import BenefitsPageDocs from './BenefitsPageDocs';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import PersonnelBenifitFormData from 'src/models/personnel/PersonnelBenefitFormData';
import useFetch from 'use-http';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PagedResultSet from 'src/models/common/PagedResultSet';
import State from 'src/models/State';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import PersonnelAdvancedQuickFind from '../dialogs/PersonnelAdvancedQuickFindDialog';
import PersonnelToast from '../components/PersonnelToast';
import PersonnelPaperworkDocs from '../components/PersonnelPaperworkDocs';
import PersonnelInsuranceDocs from '../components/PersonnelInsuranceDocs';
import PersonnelPaperworkDialog from '../dialogs/paperwork/PersonnelPaperworkDialog';
import DocumentManagement from 'src/components/layout/DocumentManagement';
import { DocumentTypes } from 'src/models/DocumentTypes';
import { PersonnelDocumentTypes, PersonnelInsuranceTypes } from 'src/models/DocumentTypesTSDI';
import PaperworkCommentDialog from '../dialogs/paperwork/PaperworkCommentsDialog';
import AddAccessAccountDialog from '../dialogs/AddAccessAccountDialog';
import TSDILandman from 'src/models/personnel/TSDILandman';
import TSDILandmanWC from 'src/models/personnel/TSDILandmanWC';
import UserAccessAccount from 'src/models/AccessAccountDetails';

const BenefitsPage = () => {
    let selectedPersonnel = useSelector((state: IRootState) => state.personnel.selectedPersonnel);
    const [states, setStates] = useState<PagedResultSet<State>>(undefined);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [accessAccountStatus, setAccessAccountStatus] = useState<string>('');
    const dispatch = useDispatch();
    const { register, errors, setValue, handleSubmit, reset } = useForm<PersonnelBenifitFormData>();
    const { get, put, post, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [paperworkProp, setPaperworkProp] = useState<{ category: string; type: string, id?: number; } | null>(null);
    const [insuranceRefreshProp, setInsuranceRefreshProp] = useState<number>(0); // manual triggers to refresh insurance information
    const [paperworkRefreshProp, setPaperworkRefreshProp] = useState<number>(0); // manual triggers to refresh paperwork information
    const [listRefreshProp, setListRefreshProp] = useState<number>(0); // manual triggers to refresh document information
    const [wcInfo, setWcInfo] = useState<TSDILandmanWC>(undefined);

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        onCreate();
    }, [selectedPersonnel]);

    useEffect(() => {
        if (accessAccountStatus && accessAccountStatus.length > 0) {
            setValue('accessAccountStatus', accessAccountStatus);
        }
    }, [accessAccountStatus]);

    useEffect(() => {
        if (paperworkProp?.type === 'insurance') {
            setInsuranceRefreshProp(prev => prev ? prev + 1 : 0);
        } else if (paperworkProp?.type === 'paperwork') {
            setPaperworkRefreshProp(prev => prev ? prev + 1 : 0);
        }
    }, [listRefreshProp]);

    useEffect(() => {
        if (!!wcInfo) {
            setValue('withholdState', wcInfo.withholdState);
            setValue('monopolisticState', wcInfo.monopolisticState);
        }
    }, [wcInfo]);

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const onCreate = () => {
        if (selectedPersonnel) {
            reset(selectedPersonnel);
            setValue('accountStatus', null);
            setValue('accessAccountStatus', null);
            setAccessAccountStatus(selectedPersonnel?.accessAccountStatus ? 'Active' : 'Inactive');
            getAccountStatus();
            getWCInfo();
        }

        console.log('BenefitsPage onCreate()');
        setInsuranceRefreshProp(0);
        setPaperworkRefreshProp(0);
        setListRefreshProp(0);

        getStates();
    };

    const onDestroy = () => {
        console.log('BenefitsPage onDestroy()');
    };

    // state options for wc section
    const getStates = async () => {
        let result = await get('/v1.0/states');

        if (response.ok) {
            result?.items?.splice(0, 0, { id: 0, name: 'DO NOT WITHHOLD', abbreviation: 'DNW' });
            setStates(result);
        } else {
            showToast('Unable to fetch states', `rgba(255,0,0,0.5)`);
        }
    };

    const stateDropdownOptions = () => {
        return states
            ? states?.items?.map((st: State) => {
                return { value: st.abbreviation, label: st.name };
            })
            : [];
    };

    // getting account status - relies on a user object existing in LMS (which is queried from the select personnel call)
    const getAccountStatus = async () => {
        let result = await get(`v1.0/users/${selectedPersonnel?.userId}/account-status`);

        if (response.ok) {
            setValue('accountStatus', result);
        } else {
            showToast(`Failed to fetch account status`, `rgba(255,0,0,0.5)`);
        }
    };

    // getting wc info if selectedPersonnel is not an employee
    const getWCInfo = async () => {
        if (selectedPersonnel.type !== 'E') {
            let result = await get(`v1.0/landmen/personnel/${selectedPersonnel?.tsdNo}/get-wc-info`);

            if (result && response.ok) {
                setWcInfo(result);
            } else {
                showToast(`Unable to fetch wc info: ` + response, `rgba(255,0,0,0.5)`);
            }
        }
    };

    const displayTitle = () => {
        const title = selectedPersonnel ? `Paperwork - ${selectedPersonnel?.akaName ? selectedPersonnel?.akaName : selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName}` : 'Paperwork';

        return title;
    };

    const showPersonnelData = () => {
        console.log('Display the Stuffs');
    };

    // left column of screen, displays all paperwork information
    const displayPaperwork = () => {
        return <div>
            <div className="form-row">
                <p style={{ fontSize: '1.8rem' }}>Paperwork Documents</p>
                <Button
                    className="blue-hover-button"
                    style={{ width: '15%', marginLeft: 'auto' }}
                    text={'Comments'}
                    onClick={() => showDialog(DialogList.PAPERWORK_COMMENT)}
                />
            </div>

            {selectedPersonnel && <PersonnelPaperworkDocs
                showPaperworkDialog={showPaperworkDialog}
                showDocumentUploadDialog={showDocumentUploadDialog}
                showDialog={showDialog}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel ? selectedPersonnel : null}
                showToast={showToast}
                refresh={paperworkRefreshProp}
            />}
            <br />
            <div className="form-row">
                <p style={{ fontSize: '1.8rem' }}>Insurance Documents</p>
            </div>
            {selectedPersonnel && <PersonnelInsuranceDocs
                showPaperworkDialog={showPaperworkDialog}
                showDocumentUploadDialog={showDocumentUploadDialog}
                showDialog={showDialog}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel ? selectedPersonnel : null}
                showToast={showToast}
                refresh={insuranceRefreshProp}
            />}

        </div>;
    };

    const onSubmit = (data: PersonnelBenifitFormData) => {
        const finalData = finalizeData(data);
        updatePersonnel(finalData);
    };

    // update personnel wc info
    const finalizeData = (data: PersonnelBenifitFormData) => {
        const finalData: TSDILandmanWC = {
            tsdNo: selectedPersonnel.tsdNo,
            withholdState: data.withholdState,
            monopolisticState: data.monopolisticState
        };
        return finalData;
    };

    const updatePersonnel = async (data: TSDILandmanWC) => {
        if (selectedPersonnel.type !== 'E') {
            let result = await put(`/v1.0/landmen/personnel/save-wc-info`, data);

            if (response.ok) {
                showToast('Update successful!');
            } else if (result?.messageCode === '15') {
                showToast('Update failed', `rgba(255,0,0,0.5)`);
            } else {
                showToast('Update personnel failed', `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Cannot add worker\'s comp to an employee',  `rgba(255,0,0,0.5)`);
        }
    };

    const handleCreateAccessAccount = async (accountDetails: UserAccessAccount) => {
        if (accountDetails) {
            let result = await post(`/v1.0/landmen/personnel/${accountDetails.employeeNumber}/utility-account`, accountDetails);
            const errorMessage = JSON.parse(result);
            if (response.ok && result && errorMessage.error === '') {
                let updatedPersonnel = { ...selectedPersonnel, accessAccountUserName: accountDetails.userName, accessAccountStatus: true };
                selectedPersonnel = updatedPersonnel;
                dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: selectedPersonnel });
                showToast('Access Account created!');
            } else {
                showToast('Access Account creation failed', `rgba(255,0,0,0.5)`);
            }
        }
        dispatch(actions.layout.hideDialog());
    };

    const handleResetAccessAccountPassword = async () => {
        let result = await post(`/v1.0/landmen/personnel/${selectedPersonnel?.tsdNo}/utility-account/reset`);
        let jsonString = null;

        try {
            jsonString = JSON.parse(result);
        } catch {
            jsonString = null;
        }

        if (jsonString) {
            const errorMessage = jsonString;
            if (response.ok && result && errorMessage.error === '' && errorMessage.email) {
                let updatedPersonnel = { ...selectedPersonnel, accessAccountStatus: true };
                selectedPersonnel = updatedPersonnel;
                dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: selectedPersonnel });
                showToast('Access Account password reset!');
            } else {
                showToast('Access Account password reset failed', `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Access Account password reset failed', `rgba(255,0,0,0.5)`);
        }
    };

    // dialog start
    const showDialog = (dialog: DialogList | string) => {
        dispatch(actions.layout.showDialog(dialog));
    };

    const hideDialog = () => {
        setPaperworkProp(null);
        dispatch(actions.layout.hideDialog());
    };
    // dialog end

    /**
     * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
     * @param personnel - personnel payload
     */
    const setSelectedPersonnel = (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);

        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
        }
    };

    // activate / deactive user from screen (will write to three tables: LMS.User, LMS.PersonDetail, DLCTSDI.LM)
    const flipActivation = async () => {
        let status = await put(`/v1.0/users/${selectedPersonnel?.userId}/toggle-status`);

        if (response.ok) {
            let updatedPersonnel = { ...selectedPersonnel, isActive: !selectedPersonnel.active };
            selectedPersonnel = updatedPersonnel;
            dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: selectedPersonnel });
            showToast('User status has been updated: ' + `${selectedPersonnel?.active ? 'Active' : 'Deactivated'}`);
            setValue('accountStatus', status);
        } else {
            showToast(`${selectedPersonnel?.active ? 'Active' : 'Deactivated'}` + ' this user failed', `rgba(255,0,0,0.5)`);
        }
    };

    // begin dialogs
    const toggleAdvancedQuickFind = () => {
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_ADVANCED_QUICK_FIND));
    };

    const showPaperworkDialog = (category: string, type: string, id: number) => {
        setPaperworkProp({ category, type, id });
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_VIEW_PAPERWORK));
    };

    const showDocumentUploadDialog = (category: string) => {
        setPaperworkProp(prev => prev ? { ...prev, category } : { category, type: '', id: undefined });
        dispatch(actions.layout.showDialog(DialogList.DOCUMENT_MANAGEMENT));
    };

    const showAddAccessAccountDialog = () => {
        dispatch(actions.layout.showDialog(DialogList.ADD_ACCESS_ACCOUNT));
    };
    // end dialogs

    const filesUploaded = async (formData?: FormData) => {
        // getting info from formData object
        const type = formData.get('fileType') as undefined as string;
        const file = formData.get('fileObject') as undefined as string;
        const fileName = formData.get('fileName') as undefined as string;
        let expirationDate = formData.get('expirationDate') as undefined as Date;
        let completionDate = formData.get('completionDate') as undefined as Date;
        const note = formData.get('note') as undefined as string;

        let fileSize = parseInt(formData.get('fileSize') as undefined as string, 10);
        let fileContext = parseInt(formData.get('docType') as string, 10);
        let contextName = DocumentTypes.find((t) => t.id === fileContext)?.name;

        // file information for s3 upload
        let uploadFormData = new FormData();
        uploadFormData.append('docType', contextName);
        uploadFormData.append('fileName', fileName);
        uploadFormData.append('originalFileName', fileName);
        uploadFormData.append('note', note);
        uploadFormData.append('fileType', type);
        uploadFormData.append('document', file);
        uploadFormData.append('fileSize', fileSize.toString());

        if (expirationDate && expirationDate !== null) {
            uploadFormData.append('expirationDate', expirationDate.toString());
        }

        if (completionDate && completionDate !== null) {
            uploadFormData.append('completionDate', completionDate.toString());
        }

        // if dates are null
        if (uploadFormData.get('expirationDate') === 'null') {
            uploadFormData.delete('expirationDate');
            expirationDate = null;
        }

        if (uploadFormData.get('completionDate') === 'null') {
            uploadFormData.delete('completionDate');
            completionDate = null;
        }

        uploadFormData.append('fileObject', file);

        // upload file to s3
        let s3Id = await post(`/v1.0/landmen/personnel/upload-paperwork-document`, uploadFormData);

        if (response.ok) {
            uploadFormData.append('s3Id', s3Id.toString());

            // TODO: refactor form document above to not be needed
            let finalData = {
                'fileName': fileName,
                'note': note,
                'fileType': type,
                'fileSize': fileSize,
                's3Id': s3Id,
                'docNo': -1,
                'expirationDate': expirationDate?.toString(),
                'completionDate': completionDate?.toString()
            };

            // uploading to specific lm tables
            // paperwork
            let personnelDocumentType = PersonnelDocumentTypes.find((t) => t.name === contextName);

            if (personnelDocumentType !== undefined) {
                finalData.docNo = personnelDocumentType.id;

                await post(`/v1.0/landmen/personnel/${selectedPersonnel?.lmNo}/upload-personnel-paperwork`, finalData);

                if (response.ok) {
                    showToast('Upload successful');
                    setPaperworkRefreshProp(prev => prev ? prev + 1 : 1);
                } else {
                    showToast('Upload failed: ' + response, `rgba(255,0,0,0.5)`);
                }
            } else { // insurance
                let personnelInsuranceType = PersonnelInsuranceTypes.find((t) => t.name === contextName);
                if (personnelInsuranceType !== undefined) {
                    finalData.docNo = personnelInsuranceType.id;

                    await post(`/v1.0/landmen/personnel/${selectedPersonnel?.lmNo}/upload-personnel-insurance`, finalData);

                    if (response.ok) {
                        showToast('Upload successful');
                        setInsuranceRefreshProp(prev => prev ? prev + 1 : 1);
                    } else {
                        showToast('Upload failed: ' + response, `rgba(255,0,0,0.5)`);
                    }
                } else {
                    showToast('Upload failed, unable to find document type', `rgba(255,0,0,0.5)`);
                }
            }
        } else {
            showToast('Upload failed: ' + response.data, `rgba(255,0,0,0.5)`);
        }

        dispatch(actions.layout.hideDialog());
    };

    return (
        <div>
            <PersonnelNavBar />
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <PersonnelSearch showPersonnelData={showPersonnelData} setIsCollapsed={setIsCollapsed} toggleAdvancedQuickFind={toggleAdvancedQuickFind} />
            <Page>
                {/* left column of page */}
                <Page.Section width={6}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle={displayTitle()}>
                            {selectedPersonnel && <div className={isCollapsed ? 'large-panel' : 'large-panel-with-search'} style={{ overflowY: 'scroll' }}>
                                <div style={{ padding: '2rem', whiteSpace: 'nowrap' }}>
                                    {displayPaperwork()}
                                </div>
                            </div>}
                            {!selectedPersonnel && <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '20px', padding: '3rem' }}>
                                No Personnel Selected
                            </div>}
                        </Panel>
                    </Panel>
                </Page.Section>
                {/* right column of page */}
                <Page.Section width={6}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle={`User Account Maintenance`}>
                            {selectedPersonnel && <div className={isCollapsed ? 'large-panel' : 'large-panel-with-search'} style={{ overflowY: 'auto' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <h1 className="legend-title" style={{ margin: '0rem' }}>Company Account</h1>
                                </div>
                                <div style={{ padding: '2rem' }}>
                                    <RHFInputBox
                                        name={`accountStatus`}
                                        label="Account Status"
                                        type="text"
                                        register={register}
                                        errors={errors}
                                        bodyStyle={{ width: '100%' }}
                                        style={{ textAlign: 'left', width: '100%' }}
                                        disabled={true}
                                    />
                                </div>
                                <div className="form-row">
                                    <Button
                                        className="blue-hover-button"
                                        style={{ marginLeft: '2rem', width: '30.5%' }}
                                        text={'Activate/Deactivate'}
                                        onClick={() => flipActivation()}
                                    />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <h1 className="legend-title" style={{ margin: '0rem' }}>Access Account</h1>
                                </div>
                                <div style={{ padding: '2rem' }}>
                                    <RHFInputBox
                                        name={`accessAccountUserName`}
                                        label="Username"
                                        type="text"
                                        register={register}
                                        errors={errors}
                                        bodyStyle={{ width: '100%' }}
                                        style={{ textAlign: 'left', width: '100%' }}
                                        disabled={true}
                                    />
                                    <RHFInputBox
                                        name={`accessAccountStatus`}
                                        label="Account Status"
                                        type="text"
                                        register={register}
                                        errors={errors}
                                        bodyStyle={{ width: '100%' }}
                                        style={{ textAlign: 'left', width: '100%' }}
                                        disabled={true}
                                    />
                                </div>
                                <div className="form-row">
                                    <Button
                                        className="blue-hover-button"
                                        style={{ width: '30%', marginLeft: '2rem', marginRight: '1rem' }}
                                        text={'Add Account'}
                                        disabled={selectedPersonnel?.accessAccountUserName && selectedPersonnel.accessAccountUserName.length > 0}
                                        onClick={() => showAddAccessAccountDialog()}
                                    />
                                    <Button
                                        className="blue-hover-button"
                                        style={{ width: '30%', marginRight: '1rem' }}
                                        text={'Reset Password'}
                                        disabled={!(selectedPersonnel?.accessAccountUserName && selectedPersonnel.accessAccountUserName.length > 0)}
                                        onClick={() => handleResetAccessAccountPassword()}
                                    />
                                    <Button
                                        className="blue-hover-button"
                                        style={{ width: '28%' }}
                                        text={'Activate/Deactivate'}
                                        disabled={true}
                                    />
                                </div>
                                <div className="form-row">
                                    <Button
                                        className="blue-hover-button"
                                        style={{ width: '30%', marginLeft: '2rem', marginRight: '1rem' }}
                                        text={'Transfer Existing Account'}
                                        disabled={true}
                                    />
                                    <Button
                                        className="blue-hover-button"
                                        style={{ width: '30%', marginRight: '1rem' }}
                                        text={'Role Maintenance'}
                                        disabled={true}
                                    />
                                    {/* <Button
                                        className="blue-hover-button"
                                        style={{ width: '30.5%' }}
                                        text={'Add/Remove Blacklist'}
                                        onClick={() => deSelectAll()}
                                    /> */}
                                </div>

                                <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                                    <h2 className="legend-title">Workers' Comp</h2>
                                </div>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="form-row" style={{ padding: '2rem' }}>
                                        <RHFSelect
                                            name={`withholdState`}
                                            label="Withhold State"
                                            labelOnLeft={true}
                                            labelStyle={{ width: '20rem' }}
                                            register={register}
                                            errors={errors}
                                            options={stateDropdownOptions()}
                                            bodyStyle={{ width: '49%' }}
                                            style={{ width: '100%' }}
                                        />
                                        <RHFSelect
                                            name={`monopolisticState`}
                                            label="Monopolistic State"
                                            labelOnLeft={true}
                                            labelStyle={{ width: '25rem' }}
                                            register={register}
                                            errors={errors}
                                            bodyStyle={{ width: '49%' }}
                                            style={{ textAlign: 'right', width: '100%' }}
                                            options={[
                                                { value: 'ND', label: 'North Dakota' },
                                                { value: 'OH', label: 'Ohio' },
                                                { value: 'WA', label: 'Washington' },
                                                { value: 'WY', label: 'Wyoming' },
                                                { value: '', label: ''}
                                            ]}
                                        />
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '30%' }}
                                            text="Save"
                                            intent="primary"
                                            type="submit"
                                        />
                                    </div>
                                </form>
                            </div>}
                            {!selectedPersonnel && <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '20px', padding: '3rem' }}>
                                No Personnel Selected
                            </div>}
                        </Panel>
                    </Panel>
                </Page.Section>
                <DocumentManagement
                    filesUploaded={filesUploaded}
                    context={paperworkProp?.category?.toLocaleLowerCase()}
                    title={paperworkProp?.category ? 'Upload ' + paperworkProp.category : null}
                />
            </Page>
            <PersonnelAdvancedQuickFind
                dialogName={DialogList.PERSONNEL_ADVANCED_QUICK_FIND}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel}
                selectPersonnel={setSelectedPersonnel}
            />
            <PersonnelPaperworkDialog
                dialogName={DialogList.PERSONNEL_VIEW_PAPERWORK}
                type={paperworkProp?.type}
                category={paperworkProp?.category}
                categoryId={paperworkProp?.id}
                closePanel={hideDialog}
                selectedPersonnel={selectedPersonnel}
                refresh={() => setListRefreshProp(prev => prev ? prev + 1 : 0)}
            />
            <PaperworkCommentDialog
                dialogName={DialogList.PAPERWORK_COMMENT}
                lmNo={selectedPersonnel?.lmNo}
                hideDialog={hideDialog}
                showToast={showToast}
            />
            <AddAccessAccountDialog
                defaultUserName={selectedPersonnel?.akaName}
                hideDialog={hideDialog}
                tsdNumber={selectedPersonnel?.tsdNo}
                handleCreateAccessAccount={handleCreateAccessAccount}
            />
        </div>
    );
};

export default BenefitsPage;


// WEBPACK FOOTER //
// ./src/components/personnel/pages/PaperworkAccessPage.tsx